/**
 * Cost Inbox → Sends: every Xero / LiveCosts email sent for posted invoices, with the
 * mail-server delivery report (delivered / delayed / bounced) when Resend has told us.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Download, Loader2, Send, RefreshCw, ExternalLink, AlertTriangle, CheckCircle2, Clock, RotateCcw } from 'lucide-react';
import { DELIVERY } from '../components/cost-inbox/LiveCostsActions';

const STATUS = [
  { key: 'all', label: 'All' }, { key: 'attention', label: 'Needs attention' }, { key: 'sent', label: 'Sent' },
  { key: 'failed', label: 'Failed / bounced' }, { key: 'not_sent', label: 'Not sent' },
];
const KIND = [{ key: 'all', label: 'Xero + LiveCosts' }, { key: 'xero', label: 'Xero' }, { key: 'livecosts', label: 'LiveCosts' }];
const COMPANY = [{ key: 'all', label: 'Both companies' }, { key: 'DEV', label: 'LUX Developments' }, { key: 'CON', label: 'LUX Construction' }];

const fmtDT = (s) => (s ? new Date(s).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const fmtD = (s) => (s ? new Date(s).toLocaleDateString('en-GB') : '—');
const gbp = (n) => (n == null ? '—' : `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`);

function Chip({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? 'bg-[#F5A623] text-[#1E2530] border-[#F5A623] font-semibold' : 'border-white/15 text-gray-300 hover:bg-white/10'}`}>
      {children}
    </button>
  );
}

function StatusPill({ row }) {
  const s = row.status;
  if (s === 'sent') return <span className="inline-flex items-center gap-1 text-xs text-emerald-300" data-testid={`send-status-${row.item_id}-${row.kind}`}><CheckCircle2 className="w-3.5 h-3.5" /> Sent</span>;
  if (s === 'failed') return <span className="inline-flex items-center gap-1 text-xs text-red-300" data-testid={`send-status-${row.item_id}-${row.kind}`}><AlertTriangle className="w-3.5 h-3.5" /> Failed</span>;
  if (s === 'pending') return <span className="inline-flex items-center gap-1 text-xs text-gray-400" data-testid={`send-status-${row.item_id}-${row.kind}`}><Clock className="w-3.5 h-3.5" /> Sending…</span>;
  return <span className="inline-flex items-center gap-1 text-xs text-amber-300" data-testid={`send-status-${row.item_id}-${row.kind}`}><AlertTriangle className="w-3.5 h-3.5" /> Not sent</span>;
}

function DeliveryCell({ row }) {
  const d = row.delivery;
  const tid = `send-delivery-${row.item_id}-${row.kind}`;
  if (row.status !== 'sent') return <span className="text-xs text-gray-600" data-testid={tid}>—</span>;
  if (!d?.status || d.status === 'accepted') return <span className="text-xs text-gray-500" title="Resend accepted the email; no delivery report yet" data-testid={tid}>Awaiting report</span>;
  const meta = DELIVERY[d.status] || { label: d.status, tone: 'text-gray-400' };
  return (
    <span className={`text-xs ${meta.tone}`} title={d.reason || ''} data-testid={tid}>
      {meta.label.charAt(0).toUpperCase() + meta.label.slice(1)}
      <span className="text-gray-500"> · {fmtDT(d.at)}</span>
      {d.reason && <div className="text-[10px] text-gray-400 truncate max-w-[260px]">{d.reason}</div>}
    </span>
  );
}

function monthOptions(n = 18) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ key: format(d, 'yyyy-MM'), label: format(d, 'MMMM yyyy') });
  }
  return out;
}

export default function CostInboxSends() {
  const { api, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('all');
  const [kind, setKind] = useState('all');
  const [company, setCompany] = useState('all');
  const [month, setMonth] = useState('');
  const [data, setData] = useState({ rows: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const retryFailed = async () => {
    setRetrying(true);
    try {
      const [inbox, caps] = await Promise.allSettled([
        api.post('/cost-inbox/sends/retry-failed'),
        api.post('/captures/resend-unsent', null, { params: { limit: 50, since_days: 30 } }),
      ]);
      const a = inbox.status === 'fulfilled' ? inbox.value.data : null;
      const c = caps.status === 'fulfilled' ? caps.value.data : null;
      const parts = [];
      if (a) parts.push(`invoices: ${a.fixed}/${a.attempted} now sent${a.still_failed ? `, ${a.still_failed} still failing` : ''}`);
      else parts.push(`invoices: ${inbox.reason?.response?.data?.detail || 'retry failed'}`);
      if (c) parts.push(`captures: ${c.sent}/${c.attempted} sent${c.still_unsent ? `, ${c.still_unsent} still unsent` : ''}`);
      else if (caps.reason?.response?.status !== 409) parts.push(`captures: ${caps.reason?.response?.data?.detail || 'retry failed'}`);
      else parts.push(`captures: ${caps.reason.response.data?.detail}`);
      const allGood = a && a.still_failed === 0 && (!c || c.still_unsent === 0);
      (allGood ? toast.success : toast.warning)(`Retry finished — ${parts.join(' · ')}`, { duration: 9000 });
      load();
    } finally { setRetrying(false); }
  };

  useEffect(() => { if (user && !isAdmin) navigate('/projects'); }, [user, isAdmin, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      if (kind !== 'all') params.kind = kind;
      if (company !== 'all') params.company = company;
      if (month) params.month = month;
      const r = await api.get('/cost-inbox/sends', { params });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load sends'); }
    finally { setLoading(false); }
  }, [api, status, kind, company, month]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      if (kind !== 'all') params.kind = kind;
      if (company !== 'all') params.company = company;
      if (month) params.month = month;
      const r = await api.get('/cost-inbox/sends.csv', { params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `invoice-sends${month ? `_${month}` : ''}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const s = data.summary;
  return (
    <div className="min-h-screen bg-[#1E2530] text-white p-6" data-testid="cost-inbox-sends-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <button type="button" onClick={() => navigate('/cost-inbox')} className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1 mb-2" data-testid="sends-back">
              <ArrowLeft className="w-3.5 h-3.5" /> Cost Inbox
            </button>
            <h1 className="text-3xl font-bold flex items-center gap-2"><Send className="w-7 h-7 text-[#F5A623]" /> Invoice sends</h1>
            <p className="text-sm text-gray-400 mt-1">Every Xero and LiveCosts email for posted invoices. "Sent" means Resend took it; the Delivery column shows what the receiving mail server did with it.</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={month || 'all'} onValueChange={(v) => setMonth(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px] h-9 bg-[#2A3240] border-white/15 text-white text-sm" data-testid="sends-month"><SelectValue placeholder="All months" /></SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10 text-white">
                <SelectItem value="all" data-testid="sends-month-all">All months</SelectItem>
                {monthOptions().map(o => <SelectItem key={o.key} value={o.key} data-testid={`sends-month-${o.key}`}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={load} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="sends-refresh"><RefreshCw className="w-4 h-4" /></Button>
            <Button size="sm" onClick={retryFailed} disabled={retrying || loading || !(data.failed_total > 0)} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#f7b84a] disabled:opacity-50" title="Re-attempt every failed Xero / LiveCosts invoice send and any unsent captures. Destinations already sent are never sent twice." data-testid="sends-retry-failed">
              {retrying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />} Retry all failed{data.failed_total > 0 ? ` (${data.failed_total})` : ''}
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={exporting || !data.rows.length} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="sends-export-csv">
              {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} CSV
            </Button>
          </div>
        </div>

        {s && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5" data-testid="sends-summary">
            {[['Sends', s.total, 'text-white'], ['Sent', s.sent, 'text-emerald-300'], ['Delivered', s.delivered, 'text-emerald-300'],
              ['Bounced', s.bounced, 'text-red-300'], ['Failed', s.failed, 'text-red-300'], ['Not sent', s.not_sent, 'text-amber-300']].map(([l, v, t]) => (
              <div key={l} className="bg-[#2A3240] border border-white/10 rounded-lg px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-gray-500">{l}</div>
                <div className={`text-2xl font-semibold tabular-nums ${t}`}>{v}</div>
              </div>
            ))}
          </div>
        )}
        {s && data.delivery_tracking === false && (
          <div className="mb-4 text-xs text-amber-200/90 bg-amber-500/10 border border-amber-400/30 rounded-lg px-3 py-2" data-testid="sends-tracking-off">
            Delivery reports aren't switched on yet — once the Resend webhook is connected (setup guide, part C) the Delivery column fills in automatically. Until then use "Check delivery" on an invoice.
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-2">
          {STATUS.map(o => <Chip key={o.key} active={status === o.key} onClick={() => setStatus(o.key)} testId={`sends-status-${o.key}`}>{o.label}</Chip>)}
          <span className="w-px h-5 bg-white/10 mx-1" />
          {KIND.map(o => <Chip key={o.key} active={kind === o.key} onClick={() => setKind(o.key)} testId={`sends-kind-${o.key}`}>{o.label}</Chip>)}
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {COMPANY.map(o => <Chip key={o.key} active={company === o.key} onClick={() => setCompany(o.key)} testId={`sends-company-${o.key}`}>{o.label}</Chip>)}
        </div>

        <div className="bg-[#2A3240] border border-white/10 rounded-lg overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
          ) : data.rows.length === 0 ? (
            <div className="p-10 text-center text-gray-500" data-testid="sends-empty">No sends match these filters.</div>
          ) : (
            <table className="w-full text-sm" data-testid="sends-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10 [&>th]:px-3 [&>th]:py-2 [&>th]:text-left">
                  <th>To</th><th>Invoice</th><th>Company</th><th>Jobs</th><th className="text-right">Net</th><th>Posted</th><th>Status</th><th>Sent</th><th>Delivery</th><th>Sent to</th><th></th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={`${r.item_id}-${r.kind}-${r.message_id || i}`} className="border-b border-white/5 hover:bg-white/5 [&>td]:px-3 [&>td]:py-2 align-top" data-testid={`send-row-${r.item_id}-${r.kind}`}>
                    <td><span className={`text-xs px-2 py-0.5 rounded-full border ${r.kind === 'xero' ? 'border-sky-400/40 text-sky-200 bg-sky-500/10' : 'border-purple-400/40 text-purple-200 bg-purple-500/10'}`}>{r.kind === 'xero' ? 'Xero' : 'LiveCosts'}</span></td>
                    <td>
                      <div className="font-medium text-gray-100 truncate max-w-[220px]">{r.supplier_name || r.original_filename || 'invoice'}</div>
                      <div className="text-xs text-gray-400">{r.reference ? `Ref ${r.reference}` : ''}{r.invoice_date ? ` · ${fmtD(r.invoice_date)}` : ''}</div>
                    </td>
                    <td className="text-xs text-gray-300">{r.company === 'CON' ? 'LUX Construction' : r.company === 'DEV' ? 'LUX Developments' : '—'}</td>
                    <td className="text-xs text-gray-300">{(r.jobs || []).map(j => j.split('-')[0]).join(', ') || '—'}</td>
                    <td className="text-right tabular-nums text-gray-200">{gbp(r.net_pounds)}</td>
                    <td className="text-xs text-gray-400">{fmtDT(r.posted_at)}</td>
                    <td>
                      <StatusPill row={r} />
                      {r.status !== 'sent' && r.failure_reason && <div className="text-[10px] text-gray-400 max-w-[260px]">{r.failure_reason}</div>}
                      {r.resends > 0 && <div className="text-[10px] text-gray-500">re-sent {r.resends}×</div>}
                      {r.compressed && <div className="text-[10px] text-gray-500">PDF compressed{r.attachment_bytes ? ` · ${(r.attachment_bytes / 1048576).toFixed(1)} MB` : ''}</div>}
                    </td>
                    <td className="text-xs text-gray-400">{fmtDT(r.sent_at)}</td>
                    <td><DeliveryCell row={r} /></td>
                    <td className="text-xs text-gray-400 truncate max-w-[200px]" title={r.destination_email || ''}>{r.destination_email || '—'}</td>
                    <td>
                      <button type="button" onClick={() => navigate(`/cost-inbox?open=${r.item_id}`)} className="text-xs text-[#F5A623] hover:underline inline-flex items-center gap-1" data-testid={`send-open-${r.item_id}-${r.kind}`}>
                        open <ExternalLink className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {!loading && data.rows.length > 0 && <div className="text-[11px] text-gray-500 mt-2">{data.rows.length} send{data.rows.length === 1 ? '' : 's'}{month ? ` · posted ${format(new Date(`${month}-01`), 'MMMM yyyy')}` : ''}</div>}
      </div>
    </div>
  );
}
